import { effect, inject, Injectable, Injector } from '@angular/core';
import { Subject } from 'rxjs';
import type { BookFormat } from '@lite-workspace/photobook-model';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { DesignsFacade } from '../features/designs/domain/application/designs.facade';
import { PartnerShopResource } from '../features/designs/domain/infrastructure/partner-shop.resource';
import { LumirLibraryResource } from '../features/designs/domain/infrastructure/partner-showcase.resource';
import { designPackFormat, restorableDesign, type LumirLibraryPack } from '../features/designs/domain/entities/design.models';

/** «Создать проект» из любого места кабинета; `designId` — пак, с которым мастер откроется. */
export interface CreateEntry {
  designId?: string;
}

/** Пак входа, проверенный по спискам: `designId` null — пака нет или он не одобрен. */
export interface CreateEntryPack {
  designId: string | null;
  packFormat: BookFormat | null;
}

@Injectable({ providedIn: 'root' })
export class CreateEntryService {
  // Lazy: every screen with a create button injects this bus; only an entry with a pack needs the pack lists.
  private readonly injector = inject(Injector);

  readonly openProject$ = new Subject<CreateEntry | void>();

  /** Свои паки, купленные и библиотека Люмир лежат врозь. Свой и купленный пак судятся по своим спискам:
   *  библиотеку ждёт только пак, которого там нет, — её молчание не держит мастер со своим паком.
   *  Удалённый или не одобренный пак = мастер без шаблона + тост. */
  async resolvePack(designId: string): Promise<CreateEntryPack> {
    const designs = this.injector.get(DesignsFacade);
    const shop = this.injector.get(PartnerShopResource);
    // ponytail: own packs = the first loaded page only; a pack past it reads as missing — page lookup by id if that bites.
    await Promise.all([designs.loadDesigns().catch(() => undefined), this.settled(() => shop.myTemplatesFirstLoad())]);
    const held =
      designs.designs().find(design => design.id === designId) ?? shop.myTemplates().find(template => template.id === designId);
    // A held library pack already matched as a licence above, so what is left here is the offer (`libraryOffer`).
    const pack = held ?? (await this.libraryOffer(designId));
    if (!pack || !restorableDesign(pack)) {
      this.injector.get(ToastService).warn('Шаблон по ссылке не найден');
      return { designId: null, packFormat: null };
    }

    return { designId, packFormat: designPackFormat(pack) };
  }

  private async libraryOffer(designId: string): Promise<LumirLibraryPack | null> {
    const library = this.injector.get(LumirLibraryResource);
    await this.settled(() => library.firstLoad());
    // A link to a pack past the first page: read the rest only on a miss — the root list is shared, so the pick
    // that follows (`rememberLibraryPick`) finds the pack there too.
    const offered = (): LumirLibraryPack | null => library.packs().find(offer => offer.id === designId) ?? null;
    if (!offered() && library.hasMore()) await library.readAll();
    return offered();
  }

  private settled(loading: () => boolean): Promise<void> {
    return new Promise<void>(resolve => {
      const watcher = effect(
        () => {
          if (loading()) return;
          resolve();
          queueMicrotask(() => watcher.destroy());
        },
        { injector: this.injector },
      );
    });
  }
}
